// src/features/booking/lib.ts
// Pure helpers for the booking module — no React imports.

import type { Entity } from '@core/types/entity';

export interface ScopeIds {
  platformId: string;
  productId: string;
  tenantId: string;
}

/** Build a fully-populated Entity reference from an id and the current scope. */
export function makeEntity(id: string, typeName: string, scope: ScopeIds): Entity {
  return {
    type_id: '',
    type_name: typeName,
    id,
    platform_id: scope.platformId,
    tenant_id: scope.tenantId,
    product_id: scope.productId,
  };
}

/** Extract an error message suitable for display. */
export function errMessage(e: unknown, fallback: string): string {
  if (e instanceof Error && e.message) return e.message;
  return fallback;
}

// ---------------------------------------------------------------------------
// Money
// ---------------------------------------------------------------------------

export function fmtMoney(cents: number | undefined, currency: string | undefined, locale: string): string {
  if (cents === undefined || cents === null) return '';
  const amount = cents / 100;
  try {
    return new Intl.NumberFormat(locale, { style: 'currency', currency: currency || 'USD' }).format(amount);
  } catch {
    return `${amount.toFixed(2)} ${currency || ''}`.trim();
  }
}

// ---------------------------------------------------------------------------
// Date / time
// ---------------------------------------------------------------------------

export function fmtDate(iso: string | undefined, locale: string): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleDateString(locale, { year: 'numeric', month: 'short', day: 'numeric' });
}

export function fmtTime(iso: string | undefined, locale: string): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleTimeString(locale, { hour: '2-digit', minute: '2-digit' });
}

export function fmtDateTime(iso: string | undefined, locale: string): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleString(locale, { year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
}

/** `<input type="date">` value for a Date. */
export function toDateInputValue(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

/** `<input type="time">` value for a Date. */
export function toTimeInputValue(d: Date): string {
  return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
}

/** Combine date input + time input values into an ISO timestamp. */
export function dateTimeToIso(dateValue: string, timeValue: string): string {
  const [y, m, d] = dateValue.split('-').map(Number);
  const [hh, mm] = (timeValue || '00:00').split(':').map(Number);
  return new Date(y, (m ?? 1) - 1, d ?? 1, hh ?? 0, mm ?? 0).toISOString();
}

export function addDaysIso(days: number): string {
  const d = new Date();
  d.setDate(d.getDate() + days);
  return toDateInputValue(d);
}

// ---------------------------------------------------------------------------
// Durations (proto duration strings, e.g. "3600s")
// ---------------------------------------------------------------------------

export function durationToMinutes(dur: string | undefined): number | '' {
  if (!dur) return '';
  const secs = parseInt(dur, 10);
  if (Number.isNaN(secs)) return '';
  return Math.round(secs / 60);
}

export function minutesToDuration(minutes: number | ''): string | undefined {
  if (minutes === '' || minutes === undefined || Number.isNaN(minutes)) return undefined;
  return `${Math.round(Number(minutes) * 60)}s`;
}

export function durationToHours(dur: string | undefined): number | '' {
  if (!dur) return '';
  const secs = parseInt(dur, 10);
  if (Number.isNaN(secs)) return '';
  return Math.round((secs / 3600) * 100) / 100;
}

export function hoursToDuration(hours: number | ''): string | undefined {
  if (hours === '' || hours === undefined || Number.isNaN(hours)) return undefined;
  return `${Math.round(Number(hours) * 3600)}s`;
}

// ---------------------------------------------------------------------------
// Enum label maps (values are lookup keys into i18n, not display strings)
// ---------------------------------------------------------------------------

export const RESOURCE_STATUS_KEYS: Record<string, string> = {
  BOOKABLE_RESOURCE_STATUS_UNSPECIFIED: 'unspecified',
  BOOKABLE_RESOURCE_STATUS_DRAFT: 'draft',
  BOOKABLE_RESOURCE_STATUS_ACTIVE: 'active',
  BOOKABLE_RESOURCE_STATUS_PAUSED: 'paused',
  BOOKABLE_RESOURCE_STATUS_MAINTENANCE: 'maintenance',
  BOOKABLE_RESOURCE_STATUS_RETIRED: 'retired',
};

export const RESOURCE_STATUS_VARIANT: Record<string, 'default' | 'success' | 'warning' | 'error'> = {
  BOOKABLE_RESOURCE_STATUS_UNSPECIFIED: 'default',
  BOOKABLE_RESOURCE_STATUS_DRAFT: 'default',
  BOOKABLE_RESOURCE_STATUS_ACTIVE: 'success',
  BOOKABLE_RESOURCE_STATUS_PAUSED: 'warning',
  BOOKABLE_RESOURCE_STATUS_MAINTENANCE: 'warning',
  BOOKABLE_RESOURCE_STATUS_RETIRED: 'error',
};

export const BOOKING_STATUS_KEYS: Record<string, string> = {
  BOOKING_STATUS_UNSPECIFIED: 'unspecified',
  BOOKING_STATUS_PENDING_APPROVAL: 'pendingApproval',
  BOOKING_STATUS_CONFIRMED: 'confirmed',
  BOOKING_STATUS_IN_PROGRESS: 'inProgress',
  BOOKING_STATUS_COMPLETED: 'completed',
  BOOKING_STATUS_CANCELLED: 'cancelled',
  BOOKING_STATUS_REJECTED: 'rejected',
  BOOKING_STATUS_NO_SHOW: 'noShow',
  BOOKING_STATUS_PENDING_PAYMENT: 'pendingPayment',
  BOOKING_STATUS_EXPIRED: 'expired',
};

export const BOOKING_STATUS_VARIANT: Record<string, 'default' | 'success' | 'warning' | 'error'> = {
  BOOKING_STATUS_UNSPECIFIED: 'default',
  BOOKING_STATUS_PENDING_APPROVAL: 'warning',
  BOOKING_STATUS_CONFIRMED: 'success',
  BOOKING_STATUS_IN_PROGRESS: 'success',
  BOOKING_STATUS_COMPLETED: 'default',
  BOOKING_STATUS_CANCELLED: 'error',
  BOOKING_STATUS_REJECTED: 'error',
  BOOKING_STATUS_NO_SHOW: 'error',
  BOOKING_STATUS_PENDING_PAYMENT: 'warning',
  BOOKING_STATUS_EXPIRED: 'error',
};

export const WAITLIST_STATUS_KEYS: Record<string, string> = {
  WAITLIST_STATUS_UNSPECIFIED: 'unspecified',
  WAITLIST_STATUS_WAITING: 'waiting',
  WAITLIST_STATUS_OFFERED: 'offered',
  WAITLIST_STATUS_ACCEPTED: 'accepted',
  WAITLIST_STATUS_EXPIRED: 'expired',
  WAITLIST_STATUS_CANCELLED: 'cancelled',
  WAITLIST_STATUS_SKIPPED: 'skipped',
};

// ---------------------------------------------------------------------------
// Local "my waitlist" tracking
// ---------------------------------------------------------------------------
// ListWaitlistEntries requires resource-owner authz, so a customer cannot
// browse their own waitlist entries from the server. We keep a small local
// record of entries the current browser joined, so My Bookings can offer
// leave/accept actions using the entry_id returned by JoinWaitlist.

export interface LocalWaitlistEntry {
  entryId: string;
  resourceId: string;
  resourceName: string;
  productId: string;
  position: number;
  status: string;
  joinedAt: string;
}

const WAITLIST_STORAGE_KEY = 'corde.booking.myWaitlist';

export function loadLocalWaitlist(): LocalWaitlistEntry[] {
  try {
    const raw = window.localStorage.getItem(WAITLIST_STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

export function saveLocalWaitlist(entries: LocalWaitlistEntry[]): void {
  try {
    window.localStorage.setItem(WAITLIST_STORAGE_KEY, JSON.stringify(entries));
  } catch {
    // storage unavailable — entry just won't persist across reloads
  }
}

export function addLocalWaitlistEntry(entry: LocalWaitlistEntry): LocalWaitlistEntry[] {
  const current = loadLocalWaitlist().filter((e) => e.entryId !== entry.entryId);
  const next = [entry, ...current];
  saveLocalWaitlist(next);
  return next;
}

export function removeLocalWaitlistEntry(entryId: string): LocalWaitlistEntry[] {
  const next = loadLocalWaitlist().filter((e) => e.entryId !== entryId);
  saveLocalWaitlist(next);
  return next;
}
